<template>
  <div class="standard-page v-flex">
    <b-button-toolbar
      class="button-toolbar"
      aria-label="Toolbar with button groups"
    >
      <b-input-group id="search-group">
        <b-form-input
          id="search-input"
          :value="filter"
          placeholder="Search..."
          type="text"
          data-cy="search"
          @input="onFilterInput"
        />

        <b-input-group-text class="search-icon">
          <Search />
        </b-input-group-text>
      </b-input-group>

      <b-button
        v-for="action in actions"
        id="create-template-button"
        :key="action.label"
        :variant="action.outline ? 'outline-success' : 'blue'"
        :disabled="action.requireSelection"
        data-cy="create-template"
        @click="action.action"
      >
        {{ action.label }}
      </b-button>
    </b-button-toolbar>

    <div
      class="automation-filters"
      role="group"
      aria-label="Filter templates by automation"
    >
      <button
        v-for="option in automationFilters"
        :key="option.value"
        type="button"
        class="automation-pill"
        :class="{
          'automation-pill--active': automationFilter === option.value
        }"
        :aria-pressed="String(automationFilter === option.value)"
        :data-cy="`automation-filter-${option.value.toLowerCase()}`"
        @click="onAutomationFilter(option.value)"
      >
        {{ option.label }}
        <span class="automation-pill__count">{{
          automationCounts[option.value]
        }}</span>
      </button>
    </div>

    <DataTable
      :actions="[]"
      :table-data="paginatedTableData"
      :paginated="true"
      class-name="templates-table"
      data-cy="templates-list"
      :on-row-clicked="handleRowClicked"
      :current-page="currentPage"
      :page-size="pageSize"
      :total-records="totalMessageTemplates"
      :on-page-change="handlePage"
      :on-page-size-change="handlePageSize"
      :page-size-options="[50, 100]"
      :no-local-sorting="true"
      :fixed-layout="true"
      :on-sort-changed="handleSortChanged"
    >
      <template #cell(delta_days)="data">
        <!-- A group-sent template links each group to its editor; the rest of the row still opens the template. -->
        <template v-if="data.item.groups && data.item.groups.length">
          {{ data.item.groups.length === 1 ? 'Group:' : 'Groups:' }}
          <template v-for="(group, index) in data.item.groups">
            <router-link
              v-if="canManageGroups"
              :key="group.pkid"
              :to="groupLink(group)"
              class="template-group-link"
              :data-cy="`template-group-link-${group.pkid}`"
              >{{ group.name }}</router-link
            ><span v-else :key="group.pkid">{{ group.name }}</span
            ><template v-if="index < data.item.groups.length - 1">, </template>
          </template>
        </template>
        <template v-else>
          {{ formatAutomation(data.item.delta_days, data.item.type) }}
        </template>
      </template>

      <template #cell(delete)="data">
        <!-- Same icon button as the Groups list. System templates show it disabled rather than swapping in another icon. -->
        <span
          v-if="isSystemTemplate(data.item.type)"
          v-b-tooltip.hover
          title="System templates cannot be deleted"
        >
          <button
            type="button"
            class="row-delete-button"
            aria-label="System templates cannot be deleted"
            disabled
          >
            <Trash2 :size="16" />
          </button>
        </span>
        <button
          v-else
          type="button"
          class="row-delete-button"
          title="Delete template"
          aria-label="Delete template"
          :data-cy="`template-delete-${data.item.pkid}`"
          @click="handleDelete(data)"
        >
          <Trash2 :size="16" />
        </button>
      </template>

      <template #cell(dropdown)="data">
        <input
          class="dropdown-checkbox"
          type="checkbox"
          :data-cy="`dropdown-${data.item.pkid}`"
          :checked="data.item.chat_visible"
          @change="handleDropdownToggle(data.item, $event)"
        />
      </template>
    </DataTable>
  </div>
</template>

<script lang="ts">
import { canManageGroups as hasGroupRole } from '@/views/Groups/groupAccess'
import DataTable from '@/components/DataTable/DataTable.vue'
import Table from '@/components/Table/Table.vue'
import tableMixins from '@/mixins/TableMixins/tableMixins.js'
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import { Component, Mixins } from 'vue-property-decorator'
import { Search, Trash2 } from 'lucide-vue'
import {
  AUTOMATION_FILTERS,
  AUTOMATION_GROUP,
  compareTemplatesByAutomation,
  formatTemplateAutomation,
  isSystemTemplateType,
  matchesAutomationFilter,
  templateAutomationGroup,
  type TemplateGroup
} from '@/utils/templateAutomation'

interface MessageTemplate {
  id: string
  pkid: number
  name: string
  active: boolean
  chat_visible: boolean
  delta_days?: number | null
  type?: string
  groups?: TemplateGroup[]
  [key: string]: any
}

@Component({
  components: {
    Table,
    DataTable,
    Search,
    Trash2
  }
})
export default class MessageTemplateList extends Mixins(tableMixins) {
  currentPage = 1
  handlePageChange!: any
  isBusy = true
  messageTemplates: MessageTemplate[] = []
  allTemplates: MessageTemplate[] = [] // Store all templates for client-side filtering
  pageSize = 50
  filter = ''
  sortBy = ''
  sortDesc = false
  selected = []
  automationFilter: string = AUTOMATION_GROUP.ALL
  automationFilters = AUTOMATION_FILTERS

  actions = [
    {
      label: 'Create Template',
      outline: false,
      action: this.createTemplate,
      requireSelection: false
    }
  ]
  // Widths are explicit because the automation pills swap the rows out from under the table: on auto
  // layout every column re-measured against whatever the current filter left behind, so "Automation" sat
  // in four different places across the four pills. Percentages, so the columns still track the window.
  tableData = {
    headers: [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        thStyle: { width: '45%' }
      },
      {
        key: 'delta_days',
        label: 'Automation',
        sortable: true,
        thStyle: { width: '25%' }
      },
      {
        // Whether the template is offered in the Customers and Inbox dropdowns, stored by core-service;
        // the template's own `active` flag is not touched.
        key: 'dropdown',
        label: 'Quick Send',
        thStyle: { width: '15%' }
      },
      {
        label: '',
        key: 'delete',
        thStyle: { width: '15%' }
      }
    ],
    items: [] as MessageTemplate[]
  }

  totalMessageTemplates = 0
  totalPages: null | number = null

  mounted() {
    const navTitle = [{ crumbTitle: 'Templates' }]

    this.$store.dispatch('setPageTitle', navTitle)
    this.getMessageTemplates()
  }

  onFilterInput(value) {
    this.filter = value
    this.filterTemplates()
  }

  onAutomationFilter(group: string) {
    if (this.automationFilter === group) return

    this.automationFilter = group
    this.filterTemplates()
  }

  /** Counts for the pill badges, taken from the full unfiltered set so the
   * numbers don't change as you click between them. Search is deliberately not
   * applied here — the pills describe the library, not the current search. */
  get automationCounts(): Record<string, number> {
    const counts = {
      [AUTOMATION_GROUP.ALL]: this.allTemplates.length,
      [AUTOMATION_GROUP.AUTOMATED]: 0,
      [AUTOMATION_GROUP.MANUAL]: 0,
      [AUTOMATION_GROUP.SYSTEM]: 0
    }

    for (const template of this.allTemplates) {
      if (!template) continue
      counts[
        templateAutomationGroup(
          template.delta_days,
          template.type,
          template.groups?.length ?? 0
        )
      ] += 1
    }

    return counts
  }

  filterTemplates() {
    this.currentPage = 1

    const searchTerm = this.filter.trim().toLowerCase()

    // Search and the automation pills compose — picking "System" narrows
    // whatever the search box already matched, rather than replacing it.
    this.messageTemplates = this.allTemplates.filter(
      (template: MessageTemplate) => {
        if (!template) return false
        if (!matchesAutomationFilter(template, this.automationFilter))
          return false
        if (!searchTerm) return true

        return (
          !!template.name && template.name.toLowerCase().includes(searchTerm)
        )
      }
    )

    this.totalMessageTemplates = this.messageTemplates.length
    this.totalPages = Math.ceil(this.totalMessageTemplates / this.pageSize)
  }

  createTemplate() {
    this.$router.push({
      name: 'MessageTemplateEdit',
      params: { merchantID: this.$route.params.merchantID }
    })
  }

  async getMessageTemplates() {
    try {
      this.isBusy = true
      const response = await cfAxios.get(
        endpoints.merchantMessageTemplates(
          this.$store.getters.currentMerchant?.pkid
        )
      )
      this.allTemplates = response.data.templates as MessageTemplate[]
      await this.attachGroups()

      // Always re-filter, even with an empty search box: the automation pill is
      // also a filter, and a reload after a delete droped back to the full
      // list while a pill still looked selected.
      this.filterTemplates()
      this.tableData.items = this.messageTemplates
    } catch (err) {
      console.error('error', err)
    } finally {
      this.isBusy = false
    }
  }

  /**
   * names each template's groups from core-service's automations list, matched on the template pkid. A
   * failure leaves the column on its old labels rather than failing the page.
   */
  async attachGroups() {
    const merchantPkid = this.$store.getters.currentMerchant?.pkid
    if (!merchantPkid) return
    try {
      const response = await cfAxios.get(
        endpoints.groupAutomations(merchantPkid)
      )
      const byTemplate: Record<number, TemplateGroup[]> = {}
      for (const row of response.data?.automations ?? []) {
        if (!row.template_id) continue
        ;(byTemplate[row.template_id] ??= []).push({
          pkid: row.group_id,
          name: row.group_name
        })
      }
      for (const template of this.allTemplates) {
        this.$set(
          template,
          'groups',
          (byTemplate[template.pkid] ?? []).sort((a, b) =>
            a.name.localeCompare(b.name)
          )
        )
      }
    } catch (err) {
      console.error('group automations', err)
    }
  }

  formatAutomation(deltaDays: number | null | undefined, type?: string) {
    return formatTemplateAutomation(deltaDays, type)
  }

  // Groups is admin-only; other roles see the group names without a link the router would refuse.
  get canManageGroups(): boolean {
    return hasGroupRole(this.$store.getters.accessClaims?.roles)
  }

  groupLink(group: TemplateGroup) {
    return {
      name: 'GroupList',
      params: { merchantID: this.$route.params.merchantID },
      query: { group: String(group.pkid) }
    }
  }

  isSystemTemplate(type?: string) {
    return isSystemTemplateType(type)
  }

  handleSortChanged(context) {
    this.sortBy = context.sortBy || ''
    this.sortDesc = Boolean(context.sortDesc)
    this.currentPage = 1
  }

  handlePage(newPage) {
    this.currentPage = newPage
  }

  handlePageSize(newPageSize) {
    this.pageSize = newPageSize
    this.currentPage = 1
    this.totalPages = Math.ceil(this.totalMessageTemplates / this.pageSize)
  }

  async handleDropdownToggle(template: MessageTemplate, event: Event) {
    const checkbox = event.target as HTMLInputElement
    const show = checkbox.checked
    try {
      await cfAxios.put(
        endpoints.templateVisibility(
          this.$store.getters.currentMerchant?.pkid,
          template.pkid
        ),
        { show_in_dropdown: show }
      )
      template.chat_visible = show
    } catch (err) {
      console.error('template visibility', err)
      checkbox.checked = !show
    }
  }

  async handleDelete(data) {
    this.$bvModal
      .msgBoxConfirm('Are you sure you want to delete this template?', {
        title: 'Confirm',
        okVariant: 'danger'
      })
      .then(value => {
        if (!value) return
        try {
          this.axios
            .delete(
              endpoints.destroyMessageTemplate(
                this.$route.params.merchantID,
                data.item.id
              )
            )
            .then(() => {
              this.getMessageTemplates()
            })
        } catch (err) {
          console.error('error', err)
        }
      })
  }

  handleRowClicked(data) {
    this.$router.push({
      name: 'MessageTemplateEdit',
      params: {
        merchantID: this.$route.params.merchantID,
        messageTemplateID: data.id
      }
    })
  }

  get sortedTemplates(): MessageTemplate[] {
    if (!this.sortBy) return this.messageTemplates

    const direction = this.sortDesc ? -1 : 1

    return [...this.messageTemplates].sort((a, b) => {
      if (this.sortBy === 'delta_days') {
        return direction * compareTemplatesByAutomation(a, b)
      }

      return (
        direction * String(a[this.sortBy]).localeCompare(String(b[this.sortBy]))
      )
    })
  }

  get paginatedTableData() {
    return {
      headers: this.tableData.headers,
      items: this.sortedTemplates.slice(
        (this.currentPage - 1) * this.pageSize,
        this.currentPage * this.pageSize
      ) as MessageTemplate[]
    }
  }
}
</script>

<style lang="scss" scoped>
// Tokens deliberately match Inbox's .tab-strip / .tab / .tab-count (Inbox.vue)
// — the dashboard's established pattern for filtering a list by mutually
// exclusive category with counts. Keep the two in step; this is a visual clone
// on purpose, not a coincidence.
.automation-filters {
  display: flex;
  // Four labels including "Automated" outgrow a narrow window; wrapping drops
  // the trailing pill to its own line rather than crushing them all.
  flex-wrap: wrap;
  gap: 4px;
  // .button-toolbar's 25px plus #search-group's 1em (scss/main.scss): the first pill sits under the search box.
  margin: 0 25px 0 calc(25px + 1em);
  padding-bottom: 12px;
}

.automation-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 14px;
  color: $darker-gray;

  font-size: 14px;
  font-weight: 600;
  line-height: 1;

  background: transparent;
  border: 1px solid transparentize($darker-gray, 0.55);
  border-radius: 999px;
  cursor: pointer;
  transition:
    color 0.15s,
    background-color 0.15s,
    border-color 0.15s;

  // The selected pill takes no hover state, matching Inbox's .tab. Inbox gets
  // that for free: `.tab.active` ties `.tab:hover` on specificity and wins on
  // order. The BEM class here is a single class (0,1,0) and loses to `:hover`
  // (0,2,0) whatever the order, which put $blue text on the $button-blue fill.
  &:hover:not(.automation-pill--active) {
    color: $blue;

    border-color: $button-blue;
  }

  &:focus-visible {
    outline: 2px solid $bright-blue;
    outline-offset: 2px;
  }

  &--active {
    color: #fff;

    background: $button-blue;
    border-color: $button-blue;
  }

  .automation-pill__count {
    color: $darker-gray;

    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }

  // Full white, not a softened one: anything below ~90% alpha drops under the
  // 4.5:1 AA floor against $button-blue. The 11px/14px size gap carries the
  // hierarchy instead.
  &--active .automation-pill__count {
    color: #fff;
  }
}

.table-custom-header {
  margin-top: 6vh;
  padding-bottom: 20px;

  h4 {
    margin: 0;
    padding-top: 10px;

    color: $table-header-color;
    font-weight: bold;
  }

  .table-custom-header-action {
    display: flex;
    justify-content: flex-end;

    svg {
      margin: 0 2px 2px 0;

      font-size: 10px;
    }
  }
}

.justify-content-md-center {
  width: 100%;
  height: 100%;
}

.message-template-list
  .b-table-sticky-header
  > .table.b-table
  > thead
  > tr
  > th
  > div::after {
  position: absolute;
  bottom: -1px;
  left: -5px;

  display: block;
  width: 102%;
  height: 2px;

  background-color: $lighter-gray;

  content: '';
}

.message-template-list .b-table-sticky-header {
  margin-bottom: 0;
}
.message-template-table-header {
  th {
    padding: 7.5px 11px;

    color: $table-header-color;
    font-weight: bold;
    font-size: 14px;
    font-family: $body-font-family;
    line-height: 19px;
  }
}

.message-template-table-row {
  thead {
    tr {
      th {
        border: 0;
      }
    }
  }
}

.message-template-container {
  display: flex;
  justify-content: center;
}

::v-deep .templates-table tbody tr {
  cursor: pointer;
}

.search-icon svg {
  margin: 0 10px;
}

.message-template-list-content {
  max-height: 60vh;
}

.message-list-placeholder-container {
  display: flex;
  justify-content: center;
  width: 100%;
  padding-top: 47px;

  border-top: 1px solid $lighter-gray;

  .message-list-placeholder {
    width: 302px;
    height: 250px;

    background-image: url('../../../assets/messages_placeholder.svg?url');
    background-repeat: no-repeat;
  }
}

.btn-custom-mt {
  margin-left: -15px;

  color: gray;

  background-color: transparent;
  border: 1px solid $input-border-lighter;

  &:hover {
    color: gray;

    background-color: #f2f2f2;
    border-color: $input-border-lighter;
  }
}

.dropdown-checkbox {
  width: 20px;
  height: 20px;
  margin-left: 5px;

  vertical-align: middle;
}

.template-group-link {
  color: $button-blue;

  &:hover {
    color: $hover-button-blue;
  }
}

.row-delete-button {
  padding: 4px;
  color: $darker-gray;

  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: $danger;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
}

// ::v-deep leads: the table sits inside DataTable, so it never carries this component's scope id.
::v-deep .templates-table td {
  vertical-align: middle;
}

#create-template-button {
  margin: 0 5px 0 auto;
}
</style>
